import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { api, type AdminDevice } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { entityRefClass, formatBrowserDateTime } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

function formatMac(serial: string | null): string {
  if (!serial) return '—'
  const hex = serial.replace(/[^0-9a-fA-F]/g, '')
  return hex.match(/.{1,2}/g)?.join(':').toUpperCase() ?? serial
}

const emptyForm = {
  id: '',
  serial_no: '',
  name: '',
  owner: '',
  data_user: '',
  project: '',
  current_location: '',
  connected: 'null' as string,
  gps_lat: '',
  gps_long: '',
  host_address: '',
  device_port: '',
  modem_port: '',
  rego: '',
}

type FormState = typeof emptyForm

export function AdminDevicesPage() {
  const auth = useAuth()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [projectFilter, setProjectFilter] = useState('')
  const [connectedFilter, setConnectedFilter] = useState('all')

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState<AdminDevice | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [mutationError, setMutationError] = useState('')

  const buildParams = () => {
    const params: Record<string, string> = { page: String(page), page_size: String(pageSize) }
    if (projectFilter) params.project = projectFilter
    if (connectedFilter !== 'all') params.connected = connectedFilter
    return params
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-devices', page, pageSize, projectFilter, connectedFilter],
    queryFn: () => api.adminListDevices(buildParams()),
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  const createMutation = useMutation({
    mutationFn: (d: Partial<AdminDevice>) => api.adminCreateDevice(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-devices'] })
      setCreateOpen(false)
      setForm(emptyForm)
      setMutationError('')
    },
    onError: (e: Error) => setMutationError(e.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: number; data: Partial<AdminDevice> }) => api.adminUpdateDevice(id, d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-devices'] })
      setEditOpen(false)
      setSelectedDevice(null)
      setForm(emptyForm)
      setMutationError('')
    },
    onError: (e: Error) => setMutationError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.adminDeleteDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-devices'] })
      setDeleteOpen(false)
      setSelectedDevice(null)
      setMutationError('')
    },
    onError: (e: Error) => setMutationError(e.message),
  })

  const parseForm = (): Partial<AdminDevice> => {
    const num = (v: string) => (v === '' ? null : Number(v))
    return {
      id: num(form.id) as number,
      serial_no: form.serial_no || null,
      name: form.name || null,
      owner: form.owner || null,
      data_user: form.data_user || null,
      project: form.project || null,
      current_location: form.current_location || null,
      connected: form.connected === 'true' ? true : form.connected === 'false' ? false : null,
      gps_lat: num(form.gps_lat),
      gps_long: num(form.gps_long),
      host_address: form.host_address || null,
      device_port: num(form.device_port),
      modem_port: num(form.modem_port),
      rego: form.rego || null,
    }
  }

  const openEdit = (device: AdminDevice) => {
    const s = (v: string | number | null | undefined) => (v == null ? '' : String(v))
    setSelectedDevice(device)
    setForm({
      id: String(device.id),
      serial_no: device.serial_no ?? '',
      name: device.name ?? '',
      owner: device.owner ?? '',
      data_user: device.data_user ?? '',
      project: device.project ?? '',
      current_location: device.current_location ?? '',
      connected: device.connected === true ? 'true' : device.connected === false ? 'false' : 'null',
      gps_lat: s(device.gps_lat),
      gps_long: s(device.gps_long),
      host_address: device.host_address ?? '',
      device_port: s(device.device_port),
      modem_port: s(device.modem_port),
      rego: device.rego ?? '',
    })
    setMutationError('')
    setEditOpen(true)
  }

  const openDelete = (device: AdminDevice) => {
    setSelectedDevice(device)
    setMutationError('')
    setDeleteOpen(true)
  }

  const openCreate = () => {
    setForm(emptyForm)
    setMutationError('')
    setCreateOpen(true)
  }

  const updateField = (field: keyof FormState, value: string) => setForm((f) => ({ ...f, [field]: value }))

  const formFields = (isEdit: boolean) => {
    // These fields are resolved by the platform, not stored on the sensor row: owner and
    // data user come from its company and any active loan, project from the project it is
    // assigned to, GPS from the telemetry history, rego from the host trailer. Shown
    // because they are useful context; locked because HOSTS4 is where they change.
    //
    // 'always' vs 'onEdit' is the difference between "the API ignores this" and "the API
    // honours this but there is an existing value to protect". Owner and project are
    // settable when creating a sensor, where there is nothing to overwrite; on an existing
    // one, an edit here could only ever clear what HOSTS4 owns.
    const textFields: Array<{
      key: keyof FormState
      label: string
      type: string
      readOnly?: 'always' | 'onEdit'
    }> = [
      { key: 'id', label: 'ID', type: 'number' },
      { key: 'serial_no', label: 'Serial No', type: 'text' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'owner', label: 'Owner', type: 'text', readOnly: 'onEdit' },
      { key: 'data_user', label: 'Data User', type: 'text', readOnly: 'always' },
      { key: 'project', label: 'Project', type: 'text', readOnly: 'onEdit' },
      { key: 'current_location', label: 'Location', type: 'text' },
      { key: 'gps_lat', label: 'GPS Lat', type: 'number', readOnly: 'always' },
      { key: 'gps_long', label: 'GPS Long', type: 'number', readOnly: 'always' },
      { key: 'host_address', label: 'Host Address', type: 'text' },
      { key: 'device_port', label: 'Device Port', type: 'number' },
      { key: 'modem_port', label: 'Modem Port', type: 'number' },
      { key: 'rego', label: 'Rego', type: 'text', readOnly: 'always' },
    ]

    return (
      <div className="grid gap-3 py-4 max-h-[60vh] overflow-y-auto">
        {textFields.map(({ key, label, type, readOnly }) => {
          const locked = readOnly === 'always' || (readOnly === 'onEdit' && isEdit)
          return (
            <div key={key} className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right text-sm">{label}</Label>
              <Input
                type={type}
                className="col-span-3"
                disabled={locked || (key === 'id' && isEdit)}
                title={locked ? 'Set in HOSTS4 — read-only here' : undefined}
                value={form[key]}
                onChange={(e) => updateField(key, e.target.value)}
              />
            </div>
          )
        })}
        <div className="grid grid-cols-4 items-center gap-4">
          <Label className="text-right text-sm">Connected</Label>
          <Select value={form.connected} onValueChange={(v) => updateField('connected', v)}>
            <SelectTrigger className="col-span-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="null">Unknown</SelectItem>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mutationError && (
          <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
            {mutationError}
          </div>
        )}
      </div>
    )
  }

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Devices</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Project</Label>
            <Input
              placeholder="Filter by project"
              className="w-48"
              value={projectFilter}
              onChange={(e) => { setProjectFilter(e.target.value); setPage(1) }}
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Connected</Label>
            <Select value={connectedFilter} onValueChange={(v) => { setConnectedFilter(v); setPage(1) }}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" /> Add New
          </Button>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading devices...
          </div>
        )}

        {error && (
          <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
            Error loading devices: {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">ID</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Serial No</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Name</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Owner</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Data User</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Project</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Location</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Connected</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Last Seen</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">GPS</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Host</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Rego</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center text-muted-foreground">No devices found</TableCell>
                    </TableRow>
                  ) : (
                    data.items.map((device) => (
                      <TableRow key={`${device.id}-${device.name}`}>
                        <TableCell className="font-medium">
                          <Link to={buildAppHref(`/admin/devices/${device.id}`, auth)} className={entityRefClass}>{device.id}</Link>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{formatMac(device.serial_no)}</TableCell>
                        <TableCell>{device.name ?? '—'}</TableCell>
                        <TableCell>{device.owner ?? '—'}</TableCell>
                        {/* Equals Owner unless the sensor is out on an active loan. */}
                        <TableCell>{device.data_user ?? '—'}</TableCell>
                        <TableCell>{device.project ?? '—'}</TableCell>
                        <TableCell>{device.current_location ?? '—'}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            device.connected === true
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : device.connected === false
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {device.connected === true ? 'Yes' : device.connected === false ? 'No' : '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {formatBrowserDateTime(device.timeof)}
                        </TableCell>
                        <TableCell>
                          {device.gps_lat != null && device.gps_long != null ? (
                            <a
                              href={`https://www.google.com/maps?q=${device.gps_lat},${device.gps_long}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={entityRefClass}
                            >
                              {device.gps_lat}, {device.gps_long}
                            </a>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {device.host_address ? (
                            <Link to={buildAppHref(`/admin/devices/${device.id}`, auth)} className={entityRefClass}>
                              {(() => {
                                try { return new URL(device.host_address.startsWith('http') ? device.host_address : `https://${device.host_address}`).hostname } catch { return device.host_address }
                              })()}
                            </Link>
                          ) : '—'}
                        </TableCell>
                        {/* An inherited rego belongs to the trailer the sensor is bolted
                            to, not to the sensor — say so, or someone goes looking for
                            the wrong asset. */}
                        <TableCell>
                          {device.rego ? (
                            <span className="inline-flex items-center gap-1">
                              {device.rego}
                              {device.rego_is_inherited && (
                                <span
                                  className="text-xs text-muted-foreground"
                                  title="Inherited from the host this sensor is attached to"
                                >
                                  (host)
                                </span>
                              )}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(device)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openDelete(device)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {data.page} of {totalPages} ({data.total} total)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Device</DialogTitle>
            </DialogHeader>
            {formFields(false)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={() => createMutation.mutate(parseForm())} disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Device #{selectedDevice?.id}</DialogTitle>
            </DialogHeader>
            {formFields(true)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button
                onClick={() => selectedDevice && updateMutation.mutate({ id: selectedDevice.id, data: parseForm() })}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Hide Device</DialogTitle>
            </DialogHeader>
            <p className="py-4">
              Device #{selectedDevice?.id} will be hidden from active tables, not permanently deleted.
            </p>
            {mutationError && (
              <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
                {mutationError}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => selectedDevice && deleteMutation.mutate(selectedDevice.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Hide device
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
